/**
 * Report cover KPI chip tooltip builders.
 */
import type { CoverChip } from '../reportRendering/coverKpiChips';
import type { SiteRagReport } from '../../types/siteRag';
import type { CalculatedMetricExplanation } from './calculatedMetricExplanation';

const CHIP_FORMULA: Record<CoverChip['id'], string> = {
  power: 'Project intent power bucket or exact MW first; phased totals next; grid HIFLD proxy only for grid report types (prefixed Est.).',
  cooling: 'Project intent cooling strategy when set; water-stress climate proxy for water/environmental types when intent is undecided.',
  acres: 'Exact landAreaAcres or land bucket from intent; else site boundary / datasets.land acres as Est. proxy.',
  btm: 'intent.nuclearInterest (Primary / Considering / No); else phased tech mix; else power-availability BTM analysis rows as Est.',
  lcoe: 'Report lcoe field when computed; else authored lcoeDetailedAnalysis / btmDetailedAnalysis range as Est.',
  // D17 — the Financial Viability money metrics. All three read the DETERMINISTIC
  // engine stamp (`financialAnalysis.headline`, from projectFinance.recompute over
  // the saved financial model); none is proxied or LLM-authored.
  npv: 'Net present value from the deterministic engine stamp (financialAnalysis.headline.npvUsd) — the saved financial model discounted at the resolved WACC. Never proxied; a blank chip means the model could not compute an NPV.',
  irr: 'Project internal rate of return from the deterministic engine stamp (financialAnalysis.headline.irrPct), banded against the hurdle rate (preference override, else the engine WACC). Never proxied.',
  payback: 'Years to cumulative-cashflow breakeven from the deterministic engine stamp (financialAnalysis.headline.paybackYears). Never proxied.',
};

const CHIP_IMPROVE: Record<CoverChip['id'], string> = {
  power: 'Set exact MW or phased build-out on Site Configuration → Project intent so covers and portfolio capacity align.',
  cooling: 'Choose cooling strategy on project intent; for water reports upload utility/water documents to replace climate proxy.',
  acres: 'Enter land area on project intent or finalize site boundary on the map.',
  btm: 'Set nuclear / BTM interest on project intent; run power-availability for authored BTM technology options.',
  lcoe: 'Run power-availability or feasibility with financial assumptions so LCOE is computed, not proxied.',
  npv: 'Complete the Financial Model (CAPEX, OPEX, financing / WACC, PPA price and term), then regenerate the Financial Viability report so the stamp recomputes.',
  irr: 'Complete the financing side of the Financial Model (debt:equity, debt rate, equity hurdle, tax) and set the hurdle rate in the report preferences, then regenerate.',
  payback: 'Complete the Financial Model cost and revenue sides so the engine can build a cashflow, then regenerate the Financial Viability report.',
};

/** Map chip id/label to resolver chain documented in coverKpiChips.ts. */
export function explainCoverChip(
  chip: CoverChip,
  report: SiteRagReport,
): CalculatedMetricExplanation {
  const reportType = report.reportType ?? 'feasibility';
  const valueText = chip.value ?? '— (undecided / missing)';
  const estimateNote = chip.isEstimate ? ' Value is an estimate from report or dataset proxy, not attested intent.' : '';

  return {
    label: chip.label,
    formula: CHIP_FORMULA[chip.id],
    inputs: `${valueText} on ${reportType} cover (${chip.id}).${estimateNote}`.trim(),
    improve: CHIP_IMPROVE[chip.id],
  };
}
